import { Check, CircleDot, Layers3, MoveUpRight } from "lucide-react";
import { Link } from "wouter";
import { CtaBand } from "@/components/site/CtaBand";
import { PageHero } from "@/components/site/PageHero";
import { SiteLayout } from "@/components/site/SiteLayout";
import { conditionGroups, serviceAreas, serviceGroups } from "@/content/services";

export default function Services() {
  return (
    <SiteLayout>
      <PageHero
        currentPage="Services"
        eyebrow="Physical therapy services in Overland Park"
        title={<>Care built around <em>how you move.</em></>}
        description="Your symptoms matter, but they are only part of the picture. We evaluate the whole movement system, explain what we find, and choose services that support your specific goals."
        aside={
          <div className="hero-fact-card">
            <span className="hero-fact-icon"><Layers3 size={22} /></span>
            <strong>One plan. Many useful tools.</strong>
            <p>Hands-on care, targeted exercise, movement analysis, and technology are selected because they fit—not because every person receives the same protocol.</p>
          </div>
        }
      />

      <section className="page-section page-section-white" aria-labelledby="service-approach-title">
        <div className="container split-intro">
          <div>
            <p className="section-kicker">Start with the whole person</p>
            <h2 id="service-approach-title">The right service begins with the right question.</h2>
          </div>
          <div className="prose-lead">
            <p>What do you want to be able to do? What has changed? Which demands does your body need to meet at home, at work, in the gym, or in sport? Those questions guide the plan more effectively than a list of techniques alone.</p>
            <p>Your therapist may use one or several of the services below. Recommendations depend on your evaluation, clinical needs, preferences, and progress.</p>
          </div>
        </div>
      </section>

      {serviceGroups.map((group, groupIndex) => (
        <section className={groupIndex % 2 === 0 ? "page-section service-band" : "page-section page-section-white"} key={group.label} aria-labelledby={`service-group-${groupIndex}`}>
          <div className="container">
            <div className="service-group-heading">
              <span>{group.label}</span>
              <div>
                <h2 id={`service-group-${groupIndex}`}>{group.title}</h2>
                <p>{group.description}</p>
              </div>
            </div>
            <div className="service-card-grid">
              {group.services.map((service) => (
                <article className="service-card" key={service.title}>
                  <div className="service-card-top"><CircleDot size={19} aria-hidden="true" /><span>CORE service</span></div>
                  <h3>{service.title}</h3>
                  <p>{service.summary}</p>
                  <ul>
                    {service.details.map((detail) => <li key={detail}><Check size={15} aria-hidden="true" /> {detail}</li>)}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>
      ))}

      <section className="page-section conditions-section" aria-labelledby="conditions-title">
        <div className="container">
          <div className="section-heading centered">
            <p className="section-kicker">Conditions we treat</p>
            <h2 id="conditions-title">A broad range of movement and rehabilitation needs.</h2>
            <p>This list helps you understand the breadth of our work. A diagnosis alone does not determine whether a specific service is right for you; your therapist will consider the complete clinical picture.</p>
          </div>
          <div className="condition-grid">
            {conditionGroups.map((group) => (
              <article className="condition-card" key={group.title}>
                <h3>{group.title}</h3>
                <ul>{group.conditions.map((condition) => <li key={condition}>{condition}</li>)}</ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="page-section page-section-white" aria-labelledby="areas-title">
        <div className="container areas-grid">
          <div>
            <p className="section-kicker">Local care</p>
            <h2 id="areas-title">Proudly based in Overland Park.</h2>
            <p>CORE Strategies PT welcomes people from across Johnson County and the Kansas City region. Our location near 103rd Street makes the clinic accessible from several nearby communities.</p>
            <Link className="text-link" href="/contact">See location and directions <MoveUpRight size={17} /></Link>
          </div>
          <div className="area-list">
            {serviceAreas.map((area) => <span key={area}>{area}</span>)}
          </div>
        </div>
      </section>

      <CtaBand title="Tell us what you want movement to make possible." body="The office can help you decide whether an evaluation or another CORE service is the most useful next conversation." />
    </SiteLayout>
  );
}
